import { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import MaskedInput from 'react-text-mask'
import {
  background as backgroundColor,
  text as textColor,
} from 'theme/palette'
import { borderWidth } from 'theme/globals'
import typography from 'theme/typography'
import Icon from 'components/Icon'
import Label from 'components/Label'
import Stack from '@mui/material/Stack'
import { KEYS } from 'utils/constants'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

const SIZES = {
  sm: {
    height: 'inputShort',
  },
  default: {
    height: 'input',
  },
  lg: {
    height: 'inputTall',
  },
}

const InputField = styled.div.withConfig({
  shouldForwardProp: prop => ![
    'placeholder',
    'color',
    'border',
    'borderWidth',
    'borderColor',
    'fluid',
    'label',
    'labelPlacement',
    'labelAlign',
    'labelColor',
    'labelVariant',
    'variant',
    'size',
    'icon',
    // 'type',
    // 'inputmode',
    'pattern',
    'name',
    'placeholderChar',
    'bgColor',
    'font',
    'maxWidth',
    // 'value',
    // 'min',
    // 'max',
    'padLeft',
    'padRight',
    'optional',
    'optionalLabel',
    'squareLeft',
    'squareRight',
    'mask',
    'removeMaskOnChange',
    'inputProps',
    'startAdornment',
    'endAdornment',
    'selectOnFocus',
    'error',
    'forceError',
    'required',
    'decimals',
    'noNumbers',
    'autoAdvance',
    'autoFocus',
    'iconColor',
    'noUI',
    // 'onChange',
    'onNoChange',
    'onEnter',
    'onTab',
    'onDelete',
  ].includes(prop),
})`
  min-width: 1px;
  text-align: left;
  display: inline-grid;
  grid-gap: 10px;
  position: relative;
  grid-auto-flow: ${props =>
    props.labelPlacement === 'top' ? 'row' : 'column'};
  align-items: ${props => props.labelAlign};
  ${props => (props.fluid ? `width: 100%;` : '')}
`

const StyledInput = styled.input`
  ${props =>
    props.theme.internalMixins.p([
      0,
      typeof props.padRight === 'number' ? props.padRight : 0,
      0,
      typeof props.padLeft === 'number'
        ? props.padLeft
        : props.icon
        ? 1.7
        : props.type === 'search'
        ? 4.8
        : props.variant === 'line'
        ? 0.5
        : 1,
    ])}
  ${props => (props.fluid ? `width: 100%;` : '')}
  ${props =>
    props.variant === 'default'
      ? `border-radius: ${props.theme.globals.borderRadius.more}px;`
      : ''}
  min-height: ${props =>
    props.variant === 'line'
      ? '30px'
      : `${props.theme.globals.height[(SIZES[props.size]?.height)]}px`};
  ${props =>
    props.startAdornmentPadding && props.variant !== 'line'
      ? `padding-left: ${props.startAdornmentPadding +
          10 +
          (props.theme.internalMixins.isSafari ? 10 : 0)}px;`
      : ``}
  ${props =>
    props.endAdornmentPadding
      ? `padding-right: ${props.endAdornmentPadding + 10}px;`
      : ``}
  ${props =>
    props.font
      ? props.theme.typography[props.font]
      : props.theme.typography.data}
  -webkit-touch-callout: none;
  outline: 0;
  min-width: 1px;
  ${props => (props.maxWidth ? `width: ${props.maxWidth};` : '')}
  background-color: ${props =>
    props.bgColor
      ? props.theme.palette.background[props.bgColor].main
      : props.variant === 'default'
      ? props.theme.palette.background.paper.main
      : 'transparent'};
  border-color: ${props => props.theme.palette.border[props.border]};
  border-style: solid;
  border-width: ${props => props.theme.globals.borderWidth[props.borderWidth]};
  color: ${props => props.theme.palette.text[props.color]};

  ${props =>
    props.variant === 'line'
      ? `
    border-top: none;
    border-left: none;
    border-right: none;
  `
      : ''}
  transition: ${props =>
    props.theme.internalMixins.setTransition([
      'color',
      'border-color',
      'border',
    ])};

  ${props =>
    props.error ? `border-color: ${props.theme.palette.status.error} !important;` : ''}

  &:hover {
    ${props =>
      props.readOnly
        ? ''
        : `
      border-color: ${props.theme.palette.border.neutralCool_600};
    `}
  }

  &:focus {
    ${props => props.readOnly ? '' : `border-color: ${props.theme.palette.border.tertiary};`}
  }

  ${props => props.readOnly ? `pointer-events: none;`: ''}

  &[type="search"]::placeholder {
    color: ${props => props.theme.palette.text.neutralCool_700};
  }

  ${props =>
    props.hasAdornment &&
    (!props.startAdornmentPadding && !props.endAdornmentPadding)
      ? `
    color: transparent;

    &::placeholder {
      color: transparent;
    }
  `
      : ''}

  ${props =>
    props.squareLeft
      ? `
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: none;
  `
      : ''}

  ${props =>
    props.squareRight
      ? `
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: none;
  `
      : ''}

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
`

const StyledIcon = styled.span`
  ${props =>
    props.font
      ? props.theme.typography[props.font]
      : props.variant === 'line'
      ? props.theme.typography.valueSmall
      : props.theme.typography.data}
  position: absolute;
  left: ${props =>
    props.type === 'search'
      ? props.theme.internalMixins.spacing(1.5)
      : props.variant === 'line'
      ? 0
      : props.theme.internalMixins.spacing(1)}px;
  top: 0;
  bottom: 0;
  margin: auto;
  display: flex;
  align-items: center;
  color: ${props => props.theme.palette.text[props.iconColor] || 'inherit'};
`

const PATTERNS = {
  text: null,
  number: null,
  // eslint-disable-next-line
  email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  // eslint-disable-next-line
  password: /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*\.])(?=.{8,})/,
}

const defaultMaskOptions = {
  prefix: '$',
  suffix: '',
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
  allowDecimal: true,
  decimalSymbol: '.',
  decimalLimit: 2, // how many digits allowed after the decimal
  integerLimit: 5, // limit length of integer numbers
  allowNegative: false,
  allowLeadingZeroes: false,
}

export const MASKS = {
  ssn_itin: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  ssn: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  ssn_last_four: [/\d/, /\d/, /\d/, /\d/],
  itin: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  telephone: [
    /\d/,
    ' ',
    '(',
    /\d/,
    /\d/,
    /\d/,
    ')',
    ' ',
    /\d/,
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
  ],
  telephone_standard: [
    /\d/,
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
  ],
  postal_code: [/\d/, /\d/, /\d/, /\d/, /\d/],
  date: [/\d/, /\d /, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/],
  ein: [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  cc: [
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
  ],
  expiry: [/\d/, /\d/, '/', /\d/, /\d/],
  cvc: [/\d/, /\d/, /\d/],
  cvc_3: [/\d/, /\d/, /\d/],
  cvc_4: [/\d/, /\d/, /\d/, /\d/],
  currency: createNumberMask({
    ...defaultMaskOptions,
  }),
  currency_no_prefix: createNumberMask({
    ...defaultMaskOptions,
    prefix: '',
  }),
  currency_no_prefix_high_max: createNumberMask({
    ...defaultMaskOptions,
    integerLimit: 10,
    prefix: '',
  }),
  currency_no_prefix_no_decimal: createNumberMask({
    ...defaultMaskOptions,
    prefix: '',
    allowDecimal: false,
  }),
}

const checkValidity = (value, props) => {
  const { type, mask, min, max } = props
  let isValid = PATTERNS[type] ? PATTERNS[type].test(value) : true
  if (isValid && !!mask) isValid = !value?.toString?.().includes('_')
  // const numberOfDigits = MASKS[mask]?.filter(o => typeof o === 'object')?.length
  // if (isValid && !!numberOfDigits && mask !== 'currency') {
  //   value?.toString?.().length === MASKS[mask].filter(o => o === /\d/).length
  // }
  if (!value?.length) isValid = false
  if (min && parseInt(value) < min) isValid = false
  if (max && parseInt(value) > max) isValid = false
  return isValid
}

const countDecimals = value => {
  if (Math.floor(value.valueOf()) === value.valueOf()) return 0
  return value.toString().split('.')[1]?.length || 0
}

const Adornment = styled(Stack).attrs(props => ({
  display: 'grid',
  alignContent: 'center',
  position: 'absolute',
  top: '0',
  bottom: '0',
  left: props.side === 'left' ? '0' : undefined,
  right: props.side === 'right' ? '0' : undefined,
  my: 'auto',
  width: 'max-content',
  sx: {
    pointerEvents: 'none',
  },
  pl: props.side === 'left' ? 1 : undefined,
  pr: props.side === 'right' ? 1 : undefined,
}))``

const Input = ({
  required,
  error,
  forceError,
  onClick,
  onChange,
  onNoChange,
  onEnter,
  onTab,
  onDelete,
  value,
  readOnly,
  labelColor,
  labelVariant,
  squareLeft,
  squareRight,
  decimals,
  id,
  noUI,
  noNumbers,
  optionalLabel,
  autoFocus,
  placeholderChar,
  iconColor,
  ...props
}) => {
  const [inputID] = useState(id || Math.random().toString())
  const [isValid, setIsValid] = useState(checkValidity(value, props))
  const [initialValue, setInitialValue] = useState(undefined)
  const [hasChanged, setHasChanged] = useState(false)
  const startAdornmentEl = useRef(null)
  const endAdornmentEl = useRef(null)
  const [startAdornmentPadding, setStartAdornmentPadding] = useState(0)
  const [endAdornmentPadding, setEndAdornmentPadding] = useState(0)
  const [prevTabbable, setPrevTabbable] = useState(null)
  const [nextTabbable, setNextTabbable] = useState(null)

  useEffect(() => {
    formatValue()
    setIsValid(checkValidity(value, props))

    const newVal =
      typeof initialValue === 'undefined' && typeof value !== 'undefined'
        ? value
        : initialValue
    if (newVal) setInitialValue(newVal)
    if (value !== newVal && !hasChanged) setHasChanged(true)
  }, [value])

  useEffect(() => {
    if (startAdornmentEl)
      setStartAdornmentPadding(startAdornmentEl?.current?.offsetWidth)
    if (endAdornmentEl)
      setEndAdornmentPadding(endAdornmentEl?.current?.offsetWidth)
  }, [startAdornmentEl, endAdornmentEl])

  useEffect(() => {
    const allTabbable = document.querySelectorAll('.tabbable') || {}
    Object.values(allTabbable).map((el, i) => {
      if (el.id === inputID) {
        if (i - 1 >= 0) setPrevTabbable(allTabbable[i - 1])
        if (i + 1 < allTabbable.length) setNextTabbable(allTabbable[i + 1])
      }
    })
    const timer = setTimeout(() => {
      if (autoFocus) document.getElementById(inputID).focus()
    }, 200)

    return () => {
      clearTimeout(timer)
    }
  }, [])

  const formatValue = () => {
    let returnVal = value
    if (props.mask === 'telephone' && value[0] !== '1') {
      returnVal = `1${value}`
      const newValidity =
        typeof valid !== 'undefined' ? valid : checkValidity(returnVal, props)
      setIsValid(newValidity)
      onChange(returnVal, newValidity)
    }
  }

  const onChangeHandler = (e, valid) => {
    const target = e.srcElement || e.target
    const myLength = target?.value?.length

    if (decimals === 0) {
      if (
        countDecimals(e.target.value) > 0 &&
        ![KEYS.DELETE_FORWARD, KEYS.DELETE, KEYS.ARROW_DOWN].includes(e.keyCode)
      ) {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    if (props.maxLength && myLength > props.maxLength) {
      e.preventDefault()
      e.stopPropagation()
      focusNextElement()
    } else {
      let val = e.target.value
      if (props.mask && props.removeMaskOnChange) {
        val = val
          .replace('(', '')
          .replace(')', '')
          .replaceAll(' ', '')
          .replaceAll('_', '')
          .replaceAll('-', '')
          .replaceAll(',', '')
      }
      const newValidity =
        typeof valid !== 'undefined' ? valid : checkValidity(val, props)
      setIsValid(newValidity)
      if (
        props.max &&
        +val > props.max &&
        ![KEYS.DELETE_FORWARD, KEYS.DELETE, KEYS.ARROW_DOWN].includes(e.keyCode)
      ) {
        e.preventDefault()
        e.stopPropagation()
      } else {
        onChange(val, newValidity)
        setHasChanged(true)
      }

      if (props.maxLength && myLength >= props.maxLength) focusNextElement()
    }
  }

  const onFocusHandler = () => {
    props.onFocus()
    if (props.selectOnFocus) document.getElementById(id)?.select()
  }

  const onBlurHandler = () => {
    props.onBlur()
    if (typeof props.min !== 'undefined' && parseInt(value) < props.min) {
      onChangeHandler(props.min, true)
    }
  }

  const onKeyDownHandler = e => {
    if (decimals > 0) {
      if (
        countDecimals(e.target.value) >= decimals &&
        ![KEYS.DELETE_FORWARD, KEYS.DELETE, KEYS.ARROW_DOWN].includes(e.keyCode)
      ) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    if (e.keyCode === KEYS.DELETE) onDelete(e)
    if (e.keyCode === KEYS.ENTER) onEnter(e)
    if (e.keyCode === KEYS.TAB) onTab(e)
    if (e.key === value) onNoChange(e)
  }

  const onInputHandler = e => {
    if (props.mask === 'telephone' && e.target.value[0] !== '1') {
      e.stopPropagation()
      const newE = e
      newE.target.value = `1${e.target.value}`
      onChangeHandler(newE)
    }
  }

  const onKeyPressHandler = e => {
    if (decimals === 0 && !(e.charCode >= 48 && e.charCode <= 57)) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (noNumbers && (e.charCode >= 48 && e.charCode <= 57)) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const focusPrevElement = () => {
    setTimeout(() => prevTabbable?.focus(), 100)
  }

  const focusNextElement = () => {
    setTimeout(() => nextTabbable?.focus(), 100)
  }

  const renderInput = (ref = null, renderProps = {}) => {
    const notMaskProps = !ref
      ? {
          readOnly,
          value,
          onFocus: onFocusHandler,
          onBlur: onBlurHandler,
          onChange: onChangeHandler,
        }
      : {}
    return (
      <StyledInput
        id={inputID}
        className="tabbable"
        ref={ref}
        {...renderProps}
        {...props}
        {...notMaskProps}
        hasChanged={hasChanged}
        noUI={noUI}
        squareLeft={squareLeft}
        squareRight={squareRight}
        selectOnFocus={props.selectOnFocus || !!props.mask}
        error={forceError || (error && !isValid && required)}
        autoComplete={props.autoComplete}
        role={props.autoComplete === 'off' ? 'presentation' : undefined}
        placeholder={props.placeholder ? props.placeholder : ''}
        onClick={onClick}
        onKeyDown={onKeyDownHandler}
        onKeyPress={onKeyPressHandler}
        hasAdornment={!!props.startAdornment || !!props.endAdornment}
        startAdornmentPadding={startAdornmentPadding}
        endAdornmentPadding={props.type === 'date' ? 0 : endAdornmentPadding}
      />
    )
  }

  return (
    <InputField {...props}>
      {(props.label || props.optional || optionalLabel) && (
        <Label
          optional={props.optional}
          optionalLabel={optionalLabel}
          color={labelColor}
          variant={labelVariant}
        >
          {props.label}
        </Label>
      )}
      <Stack
        display="inline-grid"
        gap={1}
        alignItems="center"
        position="relative"
        color={props.color ? props.color : hasChanged ? 'text.main' : 'text.neutralCool_700'}
      >
        {props.icon && (
          <StyledIcon {...props} iconColor={iconColor}>
            {props.icon}
          </StyledIcon>
        )}
        {props.type === 'search' ? (
          <StyledIcon {...props} iconColor={iconColor}>
            <Icon icon="Search" />
          </StyledIcon>
        ) : null}
        {props.mask ? (
          <MaskedInput
            mask={props.mask ? MASKS[props.mask] : false}
            placeholderChar={placeholderChar}
            value={value}
            readOnly={readOnly}
            onClick={onClick}
            onFocus={onFocusHandler}
            onBlur={onBlurHandler}
            onChange={onChangeHandler}
            onInput={onInputHandler}
            render={renderInput}
          />
        ) : (
          renderInput()
        )}
        {!!props.startAdornment && (
          <Adornment ref={startAdornmentEl} side="left">
            {props.startAdornment}
          </Adornment>
        )}
        {!!props.endAdornment && (
          <Adornment ref={endAdornmentEl} side="right">
            {props.endAdornment}
          </Adornment>
        )}
      </Stack>
    </InputField>
  )
}

Input.propTypes = {
  id: PropTypes.string,
  placeholder: PropTypes.any,
  color: PropTypes.string,
  border: PropTypes.string,
  borderWidth: PropTypes.oneOf(Object.keys(borderWidth)),
  fluid: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  labelPlacement: PropTypes.oneOf(['top', 'left', 'right']),
  labelAlign: PropTypes.oneOf(['flex-start', 'center', 'flex-end']),
  labelColor: PropTypes.oneOf(Object.keys(textColor)),
  labelVariant: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'line']),
  size: PropTypes.oneOf(Object.keys(SIZES)),
  icon: PropTypes.any,
  type: PropTypes.oneOf([
    'text',
    'search',
    'number',
    'date',
    'year',
    'month',
    'day',
    'password',
    'email',
    'tel',
  ]),
  inputmode: PropTypes.oneOf(['numeric']),
  pattern: PropTypes.string,
  name: PropTypes.string,
  placeholderChar: PropTypes.string,
  bgColor: PropTypes.oneOf(Object.keys(backgroundColor)),
  font: PropTypes.oneOf(Object.keys(typography)),
  maxWidth: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  min: PropTypes.any,
  max: PropTypes.any,
  padLeft: PropTypes.number,
  padRight: PropTypes.number,
  optional: PropTypes.bool,
  optionalLabel: PropTypes.any,
  squareLeft: PropTypes.bool,
  squareRight: PropTypes.bool,
  mask: PropTypes.oneOf(Object.keys(MASKS)),
  removeMaskOnChange: PropTypes.bool,
  inputProps: PropTypes.object,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  selectOnFocus: PropTypes.bool,
  error: PropTypes.bool,
  forceError: PropTypes.bool,
  required: PropTypes.bool,
  decimals: PropTypes.number,
  noNumbers: PropTypes.bool,
  autoAdvance: PropTypes.bool,
  autoFocus: PropTypes.bool,
  iconColor: PropTypes.string,
  noUI: PropTypes.bool,
  onChange: PropTypes.func,
  onNoChange: PropTypes.func,
  onEnter: PropTypes.func,
  onTab: PropTypes.func,
  onDelete: PropTypes.func,
}

Input.defaultProps = {
  color: 'primary',
  border: 'main',
  borderWidth: 'thick',
  outlined: true,
  labelVariant: 'dataMicroMediumTertiaryUpper',
  labelPlacement: 'top',
  labelAlign: 'center',
  labelColor: 'mainCool',
  variant: 'default',
  size: 'default',
  type: 'text',
  placeholderChar: '_',
  // font: 'body',
  onClick: () => {},
  onFocus: () => {},
  onBlur: () => {},
  onChange: () => {},
  onNoChange: () => {},
  onEnter: () => {},
  onTab: () => {},
  onDelete: () => {},
  // icon: '$',
}

export default Input
